import React from 'react';
import { Link } from 'react-router-dom';
import authArt from '../../../images/big/91657.jpg';
import defaultLogo from '../../../images/teve-logo-wide.png';
import { useBranding } from '../../../context/BrandingContext';

/**
 * Two-column shell for the public auth pages.
 * Left: brand / marketing panel (hidden on small screens).
 * Right: the form, vertically centred on a clean surface.
 */
const HIGHLIGHTS = [
  ['fa-tower-broadcast', 'RTMP ingest', 'Push from OBS, vMix or any encoder'],
  ['fa-bolt', 'HLS & DASH delivery', 'Low-latency playback on every device'],
  ['fa-shield-halved', 'Auto-failover', 'Never show a black screen again'],
];

const AuthLayout = ({ eyebrow, title, subtitle, children, footer }) => {
  const branding = useBranding();
  const logo = branding.custom && branding.logoUrl ? branding.logoUrl : defaultLogo;
  return (
  <div className="tw-flex tw-min-h-screen tw-bg-surface">
    {/* Brand panel */}
    <aside
      className="tw-relative tw-hidden tw-w-[46%] tw-flex-col tw-justify-between tw-overflow-hidden tw-p-12 tw-text-white lg:tw-flex"
      style={{ background: 'var(--c-sidebar)' }}
    >
      <img
        src={authArt}
        alt=""
        className="tw-pointer-events-none tw-absolute tw-inset-0 tw-h-full tw-w-full tw-object-cover tw-opacity-20 tw-mix-blend-luminosity"
      />
      <div
        className="tw-pointer-events-none tw-absolute tw-inset-0"
        style={{ background: 'linear-gradient(150deg, rgba(20,21,26,0.72), rgba(242,106,27,0.28))' }}
      />

      <img src={logo} alt={branding.brandName} onError={(e)=>{e.target.src=defaultLogo;}} className="tw-relative tw-max-h-[64px] tw-w-auto tw-max-w-[220px]" />

      <div className="tw-relative">
        <h2 className="tw-max-w-md tw-text-[2.1rem] tw-font-extrabold tw-leading-tight tw-tracking-tight tw-text-white">
          Your broadcasting studio, all in one place.
        </h2>
        <p className="tw-mt-3 tw-max-w-md tw-text-white/70">
          Ingest, transcode, schedule and deliver live video and VOD — with analytics and monetization built in.
        </p>

        <ul className="tw-mt-8 tw-space-y-4">
          {HIGHLIGHTS.map(([icon, head, sub]) => (
            <li key={head} className="tw-flex tw-gap-3.5">
              <span className="tw-mt-0.5 tw-flex tw-h-9 tw-w-9 tw-flex-none tw-items-center tw-justify-center tw-rounded-lg tw-bg-white/12 tw-text-white">
                <i className={`fa-solid ${icon}`} />
              </span>
              <div>
                <div className="tw-font-semibold tw-text-white">{head}</div>
                <div className="tw-text-sm tw-text-white/65">{sub}</div>
              </div>
            </li>
          ))}
        </ul>
      </div>

      <div className="tw-relative tw-text-sm tw-text-white/55">
        © {new Date().getFullYear()} Teve · Powered by{' '}
        <a href="https://siman.ca" target="_blank" rel="noreferrer" className="tw-font-semibold tw-text-white/80 hover:tw-text-white">
          Siman Tech
        </a>
      </div>
    </aside>

    {/* Form panel */}
    <main className="tw-flex tw-flex-1 tw-flex-col tw-items-center tw-justify-center tw-px-5 tw-py-10 sm:tw-px-10">
      <div className="tw-w-full tw-max-w-[440px]">
        <Link to="/login" className="tw-mb-8 tw-block lg:tw-hidden">
          <img src={logo} alt={branding.brandName} onError={(e)=>{e.target.src=defaultLogo;}} className="tw-max-h-[40px] tw-w-auto tw-max-w-[160px]" />
        </Link>

        {eyebrow && (
          <div className="tw-mb-1.5 tw-text-xs tw-font-bold tw-uppercase tw-tracking-wider tw-text-brand">{eyebrow}</div>
        )}
        <h1 className="tw-text-2xl tw-font-bold tw-tracking-tight tw-text-heading">{title}</h1>
        {subtitle && <p className="tw-mt-1.5 tw-text-sm tw-text-content-muted">{subtitle}</p>}

        <div className="tw-mt-7">{children}</div>

        {footer && <div className="tw-mt-6 tw-text-center tw-text-sm tw-text-content-muted">{footer}</div>}

        <div className="tw-mt-10 tw-text-center tw-text-xs tw-text-content-faint">
          Powered by{' '}
          <a href="https://siman.ca" target="_blank" rel="noreferrer" className="tw-font-semibold tw-text-content-muted hover:tw-text-brand">
            Siman Tech
          </a>
        </div>
      </div>
    </main>
  </div>
  );
};

export default AuthLayout;
